import { useState } from "react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { ErrorTypography } from "components/ErrorTypography";
import { LoadingWrappedNode } from "components/LoadingWrappedNode";
import { useWriterComponentState } from "hooks/useWriterComponentState";
import { validateEmail } from "utils";

import { ClientAppEvent } from "../../../../constants";

interface Props {
  open: boolean;
  onClose: () => void;
  onNotification: (message: string) => void;
}

interface CheckEmailStep {
  email: string;
}

interface ExternalCustomerStep {
  firstName: string;
  lastName: string;
  companyName: string;
}

type ModalStep = "email" | "external-form";

interface ShipperExistsResponse {
  exists: boolean;
}

interface InternalCustomerInviteResponse {
  invite_id: number;
}

interface ExternalCustomerResponse {
  id: number;
}

export function AddCustomerModal(props: Props) {
  const { open, onClose, onNotification } = props;
  const posthog = usePostHog();

  const [modalStep, setModalStep] = useState<ModalStep>("email");
  const [emailData, setEmailData] = useState<CheckEmailStep>({ email: "" });
  const [externalData, setExternalData] = useState<ExternalCustomerStep>({
    firstName: "",
    lastName: "",
    companyName: "",
  });
  const [emailValidationError, setEmailValidationError] = useState<string>("");
  const [externalValidationErrors, setExternalValidationErrors] = useState<
    Partial<ExternalCustomerStep>
  >({});

  const normalizedEmail = emailData.email.trim().toLowerCase();

  // Email check state
  const {
    loading: checkingEmail,
    error: emailCheckError,
    write: checkEmail,
  } = useWriterComponentState({
    writeConfig: {
      method: "post",
      url: "/shipper-exists",
      body: { email: normalizedEmail },
    },
    errorMessage: "Failed to check email",
  });

  // Internal customer invite state
  const {
    loading: sendingInternalInvite,
    error: internalInviteError,
    write: sendInternalInvite,
  } = useWriterComponentState({
    writeConfig: {
      method: "post",
      url: "/supplier-network-internal-customer-invites",
      body: { email: normalizedEmail },
    },
    errorMessage: "Failed to send customer invite",
  });

  // External customer state
  const {
    loading: creatingExternalCustomer,
    error: externalCustomerError,
    write: createExternalCustomer,
    resetState: resetExternalCustomerState,
  } = useWriterComponentState({
    writeConfig: {
      method: "post",
      url: "/supplier-network-external-customers",
      body: {
        first_name: externalData.firstName,
        last_name: externalData.lastName,
        email: normalizedEmail,
        company_name: externalData.companyName,
      },
    },
    errorMessage: "Failed to add external customer",
  });

  const handleClose = () => {
    setModalStep("email");
    setEmailData({ email: "" });
    setExternalData({
      firstName: "",
      lastName: "",
      companyName: "",
    });
    setEmailValidationError("");
    setExternalValidationErrors({});
    onClose();
  };

  const handleInternalInviteSubmit = () => {
    sendInternalInvite<InternalCustomerInviteResponse>((response) => {
      posthog?.capture(ClientAppEvent.CUSTOMER_INVITE_SENT_INTERNAL, {
        invitee_email: normalizedEmail,
        invite_id: response.invite_id,
      });
      onNotification("Customer invite sent successfully!");
      handleClose();
    });
  };

  const handleEmailSubmit = () => {
    const emailError = validateEmail(emailData.email);
    if (emailError) {
      setEmailValidationError(emailError);
      return;
    }

    setEmailValidationError("");
    setExternalValidationErrors({});
    resetExternalCustomerState();
    checkEmail<ShipperExistsResponse>((response) => {
      if (response.exists) {
        handleInternalInviteSubmit();
      } else {
        setModalStep("external-form");
      }
    });
  };

  const validateExternalForm = (): Partial<ExternalCustomerStep> => {
    const errors: Partial<ExternalCustomerStep> = {};

    if (!externalData.firstName.trim()) {
      errors.firstName = "First name is required";
    }

    if (!externalData.lastName.trim()) {
      errors.lastName = "Last name is required";
    }

    if (!externalData.companyName.trim()) {
      errors.companyName = "Company name is required";
    }

    return errors;
  };

  const handleExternalFormSubmit = () => {
    const errors = validateExternalForm();
    setExternalValidationErrors(errors);

    if (Object.keys(errors).length === 0) {
      createExternalCustomer<ExternalCustomerResponse>((response) => {
        posthog?.capture(ClientAppEvent.CUSTOMER_ADDED_EXTERNAL, {
          customer_email: normalizedEmail,
          customer_first_name: externalData.firstName,
          customer_last_name: externalData.lastName,
          customer_company_name: externalData.companyName,
          external_customer_id: response.id,
        });
        onNotification("External customer added successfully!");
        handleClose();
      });
    }
  };

  const handleBackToEmail = () => {
    setModalStep("email");
    setExternalValidationErrors({});
  };

  const isEmailStepValid =
    emailData.email.trim() !== "" && !emailValidationError;
  const isExternalFormValid = Object.keys(validateExternalForm()).length === 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle component="div">
        <Typography variant="h5">
          {modalStep === "email" && "Add Customer"}
          {modalStep === "external-form" && "Customer Details"}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {modalStep === "email" && (
          <Stack spacing={3} mt={1}>
            <Typography variant="body2" color="text.secondary">
              Enter the email address of the customer you would like to add to
              your network.
            </Typography>
            <TextField
              label="Customer Email"
              type="email"
              value={emailData.email}
              onChange={(e) => {
                setEmailData({ email: e.target.value });
                if (emailValidationError) setEmailValidationError("");
              }}
              required
              fullWidth
              placeholder="customer@company.com"
              error={!!emailValidationError}
              helperText={emailValidationError}
            />
            <ErrorTypography error={emailCheckError} />
            <ErrorTypography error={internalInviteError} />
          </Stack>
        )}

        {modalStep === "external-form" && (
          <Stack spacing={3} mt={1}>
            <Typography variant="body2" color="text.secondary">
              This customer is not yet on Evotrux. Please provide their details
              to add them as an external customer.
            </Typography>

            <TextField
              label="Email"
              type="email"
              value={emailData.email}
              disabled
              fullWidth
            />

            <TextField
              label="First Name"
              value={externalData.firstName}
              onChange={(e) => {
                setExternalData({ ...externalData, firstName: e.target.value });
                if (externalValidationErrors.firstName) {
                  setExternalValidationErrors({
                    ...externalValidationErrors,
                    firstName: undefined,
                  });
                }
              }}
              required
              fullWidth
              placeholder="John"
              error={!!externalValidationErrors.firstName}
              helperText={externalValidationErrors.firstName}
            />

            <TextField
              label="Last Name"
              value={externalData.lastName}
              onChange={(e) => {
                setExternalData({ ...externalData, lastName: e.target.value });
                if (externalValidationErrors.lastName) {
                  setExternalValidationErrors({
                    ...externalValidationErrors,
                    lastName: undefined,
                  });
                }
              }}
              required
              fullWidth
              placeholder="Doe"
              error={!!externalValidationErrors.lastName}
              helperText={externalValidationErrors.lastName}
            />

            <TextField
              label="Company Name"
              value={externalData.companyName}
              onChange={(e) => {
                setExternalData({
                  ...externalData,
                  companyName: e.target.value,
                });
                if (externalValidationErrors.companyName) {
                  setExternalValidationErrors({
                    ...externalValidationErrors,
                    companyName: undefined,
                  });
                }
              }}
              required
              fullWidth
              placeholder="Acme Logistics"
              error={!!externalValidationErrors.companyName}
              helperText={externalValidationErrors.companyName}
            />

            <ErrorTypography error={externalCustomerError} />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {modalStep !== "email" && (
          <Button
            onClick={handleBackToEmail}
            disabled={
              checkingEmail || sendingInternalInvite || creatingExternalCustomer
            }
          >
            Back
          </Button>
        )}
        {modalStep === "email" && (
          <Button
            onClick={handleClose}
            disabled={
              checkingEmail || sendingInternalInvite || creatingExternalCustomer
            }
          >
            Cancel
          </Button>
        )}

        {modalStep === "email" && (
          <LoadingWrappedNode loading={checkingEmail}>
            <Button
              variant="contained"
              onClick={handleEmailSubmit}
              disabled={!isEmailStepValid || checkingEmail}
            >
              Continue
            </Button>
          </LoadingWrappedNode>
        )}

        {modalStep === "external-form" && (
          <LoadingWrappedNode loading={creatingExternalCustomer}>
            <Button
              variant="contained"
              onClick={handleExternalFormSubmit}
              disabled={!isExternalFormValid || creatingExternalCustomer}
            >
              Add Customer
            </Button>
          </LoadingWrappedNode>
        )}
      </DialogActions>
    </Dialog>
  );
}
